import { useData } from "./useData";
import { useCurrentUserCompany } from "./useCurrentUserCompany";

interface IPMAStationData {
  id: string;
  evaporationRate: number;
  precipitationRate: number;
  creationDate: Date;
  updatedDate: Date;
  name: string;
}

interface UseIPMAStationProps {
  weatherStationReference?: string;
  activityType?: "extraction" | "transformation";
}

export const useIPMAStation = ({ weatherStationReference, activityType }: UseIPMAStationProps = {}) => {
  const { company } = useCurrentUserCompany();
  
  const {
    data: ipmaStations,
    loading,
    error,
  } = useData<IPMAStationData>({
    collectionName: "setup_ipmaStations",
  });

  // Determine which weather station reference to use
  const getWeatherStationReference = () => {
    if (!company) return weatherStationReference;
    
    // For mixed companies, use activity-specific stations
    if (company.activityType === "extraction_and_transformation") {
      if (activityType === "extraction") {
        return company.extractionWeatherStationReference;
      } else if (activityType === "transformation") {
        return company.transformationWeatherStationReference;
      }
      // If no activity type specified, default to extraction
      return company.extractionWeatherStationReference;
    }
    
    // For single activity companies, use the provided weather station reference or company's main station
    return weatherStationReference || company.weatherStationReference;
  };

  const effectiveWeatherStationReference = getWeatherStationReference();
  
  // Find the IPMA station that matches the weather station reference
  const currentStation = ipmaStations?.find((station) => station.id === effectiveWeatherStationReference);

  return {
    ipmaStation: currentStation,
    loading,
    error,
    evaporationRate: currentStation?.evaporationRate,
    precipitationRate: currentStation?.precipitationRate,
    weatherStationReference: effectiveWeatherStationReference,
    // Helper to get rates for a specific activity type
    getRatesForActivity: (activity: "extraction" | "transformation") => {
      if (!company || company.activityType !== "extraction_and_transformation") {
        return {
          evaporationRate: currentStation?.evaporationRate,
          precipitationRate: currentStation?.precipitationRate,
        };
      }
      
      const stationRef = activity === "extraction" 
        ? company.extractionWeatherStationReference 
        : company.transformationWeatherStationReference;
      
      const station = ipmaStations?.find((s) => s.id === stationRef);
      
      return {
        evaporationRate: station?.evaporationRate,
        precipitationRate: station?.precipitationRate,
      };
    },
  };
};
